<template>
  <div id="analysis">
    <v-container>
      <v-row>
        <v-col cols="12" xs="12" md="8" sm="6" lg="6" xl="6">
          <codemirror
            ref="analysisCodeMirror"
            :value="query"
            :options="cmOptions"
            :events="['save']"
            @input="onCmCodeChange"
          />
        </v-col>
        <v-col
          cols="12"
          xs="12"
          sm="6"
          md="6"
          lg="6"
          xl="6"
          :class="{ 'hidden-sm-and-down': !showHelpOnMobile }"
        >
          <v-card flat>
            <v-tabs v-model="tab">
              <v-tab key="columns">Columns</v-tab>
              <v-tab key="recent">Recent Queries</v-tab>
              <v-tab key="examples">Examples</v-tab>
            </v-tabs>
            <v-card-text>
              <v-tabs-items v-model="tab">
                <v-tab-item key="columns">
                  <ul>
                    <li
                      v-for="column in cmOptions.hintOptions.tables.bet_history"
                      :key="column"
                    >
                      {{ column }}
                    </li>
                  </ul>
                </v-tab-item>
                <v-tab-item key="recent">
                  <codemirror
                    :value="recentQueries"
                    :options="exampleCMOptions"
                  />
                </v-tab-item>
                <v-tab-item key="examples" class="examples">
                  <codemirror :value="exampleSQL" :options="exampleCMOptions" />
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <div class="actions">
      <v-btn class="mx-2" fab dark small color="green" @click="runQuery">
        <v-icon dark>
          mdi-play
        </v-icon>
      </v-btn>

      <v-btn
        class="mx-2 hidden-md-and-up"
        fab
        dark
        small
        color="blue"
        @click="() => (showHelpOnMobile = !showHelpOnMobile)"
      >
        <v-icon dark v-if="showHelpOnMobile">
          mdi-chevron-up
        </v-icon>

        <v-icon dark v-else>
          mdi-help
        </v-icon>
      </v-btn>
    </div>

    <div class="results">
      <h3>Results</h3>
      <v-data-table
        :items="results"
        :headers="headers"
        :items-per-page="-1"
        :loading="executing"
      />
    </div>
  </div>
</template>

<script>
import { codemirror } from "vue-codemirror";
import { query } from "../lib/analysis";

// require styles
import "codemirror/lib/codemirror.css";
// language js
import "codemirror/mode/sql/sql.js";
import "codemirror/addon/hint/show-hint.js";
import "codemirror/addon/hint/show-hint.css";
import "codemirror/addon/hint/sql-hint.js";
import "codemirror/addon/comment/comment.js";

import "../lib/codemirror-addon/lang-athena-sql.js";

export default {
  components: {
    codemirror
  },
  data() {
    return {
      tab: null,
      executing: false,
      showHelpOnMobile: false,
      headers: [
        {
          text: "Bookie",
          value: "bookie"
        },
        {
          text: "EV",
          value: "EV"
        }
      ],
      results: [],
      query: "SELECT * FROM bet_history LIMIT 5",
      exampleCMOptions: {
        readOnly: true,
        mode: "text/x-aws-athena-sql"
      },
      cmOptions: {
        readOnly: false,
        tabSize: 4,
        mode: "text/x-aws-athena-sql",
        lineNumbers: true,
        line: true,
        extraKeys: {
          "Ctrl-Space": "autocomplete",
          "Ctrl-Enter": cm => {
            cm.__refresh();
          }
        },
        hintOptions: {
          tables: {
            bet_history: [
              "bet_id",
              "date",
              "bot",
              "bookie",
              "race_type",
              "race_state",
              "race_venue",
              "market",
              "bet_outcome",
              "bet_reject_reason",
              "bet_result",
              "bet_return",
              "profit",
              "attempted_stake",
              "accepted_stake",
              "bet_placement_time",
              "bet_processing_time",
              "time_before_advertised_start",
              "bookie_price",
              "betfair_price",
              "expected_margin",
              "profitable_betfair_volume",
              "bsp",
              "ev",
              "actual_margin"
            ]
          },
          defaultTable: "bet_history"
        }
      },
      recentQueries: "",
      exampleSQL: `-- Get the number of accepted bets per bookie
SELECT bookie, COUNT(bet_id) AS "Number of bets"
FROM bet_history
WHERE bet_outcome='ACCEPTED'
GROUP BY bookie
ORDER BY COUNT(bet_id) DESC

-- Average EV by bookie
SELECT bookie, AVG(ev) AS "Average EV"
FROM bet_history
WHERE bet_outcome='ACCEPTED'
GROUP BY bookie
ORDER BY AVG(ev) DESC

-- Most profitable race types
SELECT race_type, ROUND(SUM(profit), 2) AS "Total Profit"
FROM bet_history
WHERE bet_outcome='ACCEPTED'
GROUP BY race_type
ORDER BY SUM(profit) DESC

-- Average EV and Profit by bookie when
-- betting more than 5 minutes out
SELECT
  bookie,
  AVG(ev) AS "Average EV",
  AVG(profit) AS "Average Profit"
FROM bet_history
WHERE bet_outcome='ACCEPTED'
  AND time_before_advertised_start > 300
GROUP BY bookie
ORDER BY 1 DESC


-- Total Fav vs Field profit by year
SELECT
  SUBSTR(date, 1, 4) AS "Year",
  ROUND(SUM(profit), 2) AS "Total Profit"
FROM bet_history
WHERE bet_outcome='ACCEPTED'
  AND market = 'FAV_FIELD'
GROUP BY SUBSTR(date, 1, 4)
ORDER BY 1


`
    };
  },
  methods: {
    onCmCodeChange(newQuery) {
      this.query = newQuery;
    },
    async runQuery() {
      if (this.executing) return;
      this.executing = true;

      this.results = [];
      try {
        console.log("running query");
        const r = await query(this.query);
        this.results = r.result.Items;
        this.headers = Object.keys(r.result.Items[0]).map(k => ({
          text: k,
          value: k
        }));

        this.addToRecent(this.query);
      } catch (err) {
        this.showError(err?.response?.data?.message || err.message, {
          timeout: 5000
        });
      } finally {
        this.executing = false;
      }
    },
    addToRecent(query) {
      const queriesJSON = localStorage.getItem("recent-queries");
      try {
        const queries = queriesJSON ? JSON.parse(queriesJSON) : [];

        const newQueries = [query, ...queries.filter(q => q !== query)].slice(
          0,
          10
        );
        localStorage.setItem("recent-queries", JSON.stringify(newQueries));
        this.recentQueries = newQueries.join("\n\n\n");
      } catch (err) {
        console.error(err);
      }
    },
    updateRecentQueries() {
      const queriesJSON = localStorage.getItem("recent-queries");
      if (!queriesJSON) return "";
      try {
        const queries = JSON.parse(queriesJSON);

        this.recentQueries = queries.join("\n\n\n");
      } catch (err) {
        console.error(err);
      }
    }
  },
  computed: {
    codemirror() {
      return this.$refs.analysisCodeMirror.codemirror;
    }
  },
  mounted() {
    this.codemirror.__refresh = this.runQuery;
    this.updateRecentQueries();
  }
};
</script>

<style lang="scss">
#analysis {
  .actions {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  ul {
    columns: 2;
    -webkit-columns: 2;
    -moz-columns: 2;
    list-style: none;
  }

  .v-tabs-items {
    height: 300px;
  }

  .examples {
    .CodeMirror {
      // height: 50px;
    }
  }
}
</style>
